import React, { useState, useRef, useEffect } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const Select: React.FC<SelectProps> = ({ options, value, onChange, placeholder = 'Select…', className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = value ? options.find((o) => o.value === value) : undefined;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={`relative inline-block min-w-[140px] ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-a-border bg-white px-4 py-2 text-sm focus:outline-none"
      >
        <span className={`font-semibold ${selected ? 'text-a-navy' : 'text-a-text-secondary'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`h-3 w-3 flex-shrink-0 text-a-text-secondary transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <ul
          className="absolute left-0 top-full z-50 mt-1 overflow-hidden rounded-lg border border-a-border bg-white shadow-lg"
          style={{
            minWidth: '100%',
            width: 'max-content',
            maxWidth: 'calc(100vw - 3rem)',
            maxHeight: '20rem',
            overflowY: 'auto',
          }}
        >
          {options.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                style={{ whiteSpace: 'nowrap' }}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-a-off-white ${
                  opt.value === value ? 'font-semibold text-a-navy' : 'text-a-navy'
                }`}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Select;
